import CustomButton from "components/Atoms/CustomButton"
import CustomCard from "components/Atoms/CustomCard"
import CustomCheckbox from "components/Atoms/CustomCheckbox"
import CustomFormGroup from "components/Atoms/CustomFormGroup"
import CustomSpinner from "components/Atoms/CustomSpinner"
import StaticSelector from "components/Atoms/StaticSelector"
import StringInput from "components/Atoms/StringInput"
import DynamicMultiSelector from "components/Molecules/DynamicMultiSelector"
import DynamicSelector from "components/Molecules/DynamicSelector"
import FormController from "components/Molecules/FormController"
import IconSelector from "components/Molecules/IconSelector"
import MutationOptions from "components/Organisms/MutationOptions"
import {
  ADD_DEPARTMENT,
  DEPARTMENT_DETAILS,
  DEPARTMENT_LIST,
} from "graphql/department.graphql"
import {
  ALL_DEPARTMENTS as DEPARTMENTS,
  DEPOTS,
  VARIETIES,
} from "graphql/selector.queries.gql"
import { useFormDisabled } from "hooks/useAuth"
import { getErrors } from "hooks/useErrors"
import useRouteForm from "hooks/useRouteForm"
import { get, isString, pick } from "lodash-es"
import departmentModel from "models/department.model"
import React, { useMemo } from "react"
import { useNavigate, useParams } from "react-router-dom"
import AppToaster, { ApolloErrorToast } from "utils/AppToaster"
import { updateList } from "utils/apollo.utils"
import { getId } from "utils/custom.utils"

const departmentTypes = [
  {
    name: "Export Department",
    id: "EX",
  },
  {
    name: "Processing Department",
    id: "PR",
  },
  {
    name: "Retail Department",
    id: "RT",
  },
  {
    name: "Wholesale Department",
    id: "WH",
  },
]

const FLAGS = [
  {
    id: "togoSubscription",
    title: "Togo Subscription",
    helperText: (
      <span>
        If enabled, farmers registered for given varieties will be{" "}
        <span className="vt-highlight">automatically pushed for Togo</span>.
      </span>
    ),
  },
  {
    id: "generatePoAfterInward",
    title: "Generate PO On Inward",
    helperText: (
      <span>
        If enabled, POs will be generated{" "}
        <span className="vt-warning">on inward</span>.
      </span>
    ),
  },
  {
    id: "generatePoPlotWise",
    title: "Auto Populate Rate",
    helperText: (
      <span>
        if enabled, rates will be fetched from last successful PO to auto
        populate in next schedule.
      </span>
    ),
  },
  {
    id: "controlPricing",
    title: "Control Pricing",
    helperText: (
      <span>
        If enabled, Schedules will be generated only after{" "}
        <span className="vt-highlight">
          price is approved for raised Schedule Requests
        </span>
        .
      </span>
    ),
  },
  {
    id: "qcSubscription",
    title: "QC Subscription",
    helperText: (
      <span>
        If enabled, Inwards{" "}
        <span className="vt-highlight">
          cannot be submitted until QC is complete
        </span>
        .
      </span>
    ),
  },
]
const DepartmentRegistration = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [department, setDepartment, { loading }] = useRouteForm(
    DEPARTMENT_DETAILS,
    {
      transform(state) {
        return {
          ...state,
          departmentType: departmentTypes.find(
            d => d.id === state.departmentType
          ),
        }
      },
    }
  )

  function handleChange(path, value) {
    setDepartment(prevDetails => ({
      ...prevDetails,
      [path]: value,
    }))
  }

  const parsedVariables = useMemo(
    () => ({
      ...pick(department, [
        "id",
        "name",
        "departmentType",
        "photo",
        ...FLAGS.map(flag => flag.id),
      ]),
      varieties: department?.varieties?.map(getId),
      preferredDepot: department?.preferredDepot?.id,
      defaultTraderVarieties: department?.defaultTraderVarieties?.map(getId),
      photo: isString(department?.photo)
        ? department?.photo
        : department?.photo?.url?.svg,
      departmentType: department?.departmentType?.id,
    }),
    [department]
  )

  function showToast(data) {
    const deptName = get(data, "addUpdateDepartment.department.name")
    AppToaster.show({
      message: `Department ${deptName} Added Successfully`,
      intent: "success",
    })
  }
  const errors = useMemo(() => {
    return getErrors({ schema: departmentModel, state: department })
  }, [department])

  const disabled = useFormDisabled("department", parsedVariables)
  if (loading) return <CustomSpinner />

  return (
    <CustomCard className="vt-department-registration">
      <div className="mt-2 w-full flex items-center">
        <span>
          <CustomButton
            icon="arrow-left"
            onClick={() => navigate(-1)}
            minimal={true}
          ></CustomButton>
        </span>
        <h5 className="ml-3">{parseInt(id) ? "Edit" : "New"} Department</h5>
      </div>
      <FormController
        errors={errors}
        source={department}
        onChange={handleChange}
        disabled={disabled}
        disableAll={true}
      >
        <div className="container mt-3">
          <div className="form-grid">
            <StringInput
              placeholder="Name of the department"
              helperText="eg. Grapes"
              label="Name"
              path="name"
            ></StringInput>
            <StaticSelector
              big={true}
              label="Department Type"
              path="departmentType"
              items={departmentTypes}
            />
            <DynamicSelector
              big={true}
              label="Preffered Depot"
              query={DEPOTS}
              path="preferredDepot"
            />
            <DynamicMultiSelector
              fill
              big
              formClassName="col-span-2"
              label="Varieties"
              renderTag={item => {
                return `${item.commodity && item.commodity.name} | ${item.name}`
              }}
              renderText={item => item.commodity.name}
              renderLabel={item => item.name}
              query={VARIETIES}
              variables={{
                includeCommodity: true,
              }}
              path="varieties"
              searchBy={["name", "commodity.name"]}
            />
            <IconSelector label="Department Photo" path="photo" />
            <DynamicMultiSelector
              fill
              big
              formClassName="col-span-2"
              label="Default Trader Varieties"
              renderTag={item => {
                return `${item.commodity && item.commodity.name} | ${item.name}`
              }}
              renderText={item => item.commodity.name}
              renderLabel={item => item.name}
              query={VARIETIES}
              variables={{
                includeCommodity: true,
              }}
              path="defaultTraderVarieties"
              searchBy={["name", "commodity.name"]}
            />
          </div>
          {FLAGS.map(flag => {
            return (
              <React.Fragment key={flag.id}>
                <CustomFormGroup
                  big
                  helperText={flag.helperText}
                  className="w-max"
                >
                  <CustomCheckbox label={flag.title} path={flag.id} />
                </CustomFormGroup>
                <hr />
              </React.Fragment>
            )
          })}
          <MutationOptions
            alert={
              <div>
                <p>Are you sure you want to save the department?</p>
                <ul className="mb-4">
                  {FLAGS.map(flag => {
                    const isEnabled = department[flag.id]
                    return (
                      <li key={flag.id}>
                        {flag.title} -
                        <b
                          className={
                            isEnabled === true
                              ? "text-green"
                              : isEnabled === false
                              ? "text-blue-400"
                              : null
                          }
                        >
                          {isEnabled ? "Enabled" : "Disabled"}
                        </b>
                      </li>
                    )
                  })}
                </ul>
              </div>
            }
            addConfirmation
            className="mt-5"
            disabled={disabled}
            mutation={ADD_DEPARTMENT}
            variables={parsedVariables}
            onError={ApolloErrorToast}
            validate={() => {
              if (errors.latest) {
                AppToaster.show({
                  message: errors.latest,
                  intent: "danger",
                  icon: "cross",
                })
                return false
              }
              return true
            }}
            refetchQueries={[
              {
                query: DEPARTMENT_LIST,
                variables: {
                  first: 20,
                  skip: 0,
                },
              },
            ]}
            onSave={data => {
              showToast(data)
              navigate(-1)
            }}
            onSaveAndEdit={data => {
              const deptId = data?.addUpdateDepartment?.department?.id
              if (!parseInt(id))
                navigate(`/d/registration/department/${deptId}`, {
                  replace: true,
                })
            }}
            onSaveAndAdd={data => {
              showToast(data)
              setDepartment({ varieties: [] })
              navigate(`/d/registration/department/new`)
            }}
            update={(cache, { data: { addUpdateDepartment } }) => {
              const department = addUpdateDepartment.department
              updateList({
                cache,
                query: DEPARTMENTS,
                newItem: pick(department, [
                  "id",
                  "name",
                  "departmentType",
                  "__typename",
                ]),
              })
              localStorage.removeItem(`defaultTraderVarieties-${department.id}`)
              localStorage.removeItem(
                `defaultTraderVarieties-${department.id}-isChanged`
              )
            }}
          ></MutationOptions>
        </div>
      </FormController>
    </CustomCard>
  )
}

export default DepartmentRegistration
